import * as React from "react";

import { useEffect, useMemo } from "react";
import { AppSettingServices, AppSettingPermissionUpdate } from "@/services/bedrock";
import { AppSetting } from "@/services/bedrock";
import { Button, Switch, Table, Tooltip } from "@/components/ui";
import { X } from "lucide-react";
import { toast } from "sonner";
import { FormCard } from "@/components/FormCard";

interface Props {
  playerId: number | undefined;
  withWritePermission: boolean;
}

export function PlayerSettingsForm({ playerId, withWritePermission }: Props) {
  const [initialPermissions, setInitialPermissions] = React.useState<{ [key: string]: boolean | null }>({});
  const [permissions, setPermissions] = React.useState<{ [key: string]: boolean | null | undefined }>({});
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [playerSettings, setPlayerSettings] = React.useState<AppSetting[]>([]);

  React.useEffect(() => {
    AppSettingServices.getAppSettings({ isPlayerSetting: true, extraFields: "coachPermission" })
      .then(response => setPlayerSettings(response.content))
      .catch(console.error);
  }, [playerId]);

  const loadSettings = () => {
    if (!playerId || playerSettings.length === 0) return;

    setLoading(true);
    AppSettingServices.getPlayerSettingPermissions({ accountId: playerId })
      .then(response => {
        const permissions = response.content;
        const permissionEntries = playerSettings.map(setting => [
          setting.id,
          permissions.find(p => p.appSettingId === setting.id)?.value ?? null,
        ]);
        const permissionObject = Object.fromEntries(permissionEntries);
        setInitialPermissions(permissionObject);
        setPermissions(permissionObject);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const permissionUpdates = useMemo(() => {
    if (!playerId) return [];

    return Object.entries(permissions)
      .map(([appSettingId, value]) => {
        if (value === initialPermissions[appSettingId]) return null;
        return { appSettingId, accountId: playerId, value };
      })
      .filter(Boolean) as AppSettingPermissionUpdate[];
  }, [initialPermissions, permissions, playerId]);

  useEffect(() => {
    loadSettings();
  }, [playerId, playerSettings]);

  const saveSettings = async () => {
    if (!playerId) return;

    setSaving(true);
    AppSettingServices.updatePlayerSettingPermissions(permissionUpdates)
      .then(() => {
        toast.success("Settings saved!");
      })
      .catch(console.error)
      .finally(() => setSaving(false));
  };

  return (
    <FormCard>
      <FormCard.Header title="Feature Settings" description="Configure specific app features for this player" />
      <FormCard.Content>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Cell>Feature</Table.Cell>
              <Table.Cell align="center">Default Value</Table.Cell>
              <Table.Cell align="center">Player Value</Table.Cell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {playerSettings.map(setting => {
              const accountValue = setting.coachPermission?.value ?? setting.defaultValue;
              const playerValue = permissions[setting.id];
              return (
                <Table.Row key={setting.id}>
                  <Table.Cell>{setting.name.replaceAll("Players", "Player")}</Table.Cell>
                  <Table.Cell align="center">{accountValue ? "Yes" : "No"}</Table.Cell>
                  <Table.Cell align="center">
                    <div className="playerSettingOverride">
                      {playerValue === null || playerValue === undefined ? (
                        <Button
                          onClick={() => setPermissions({ ...permissions, [setting.id]: !accountValue })}
                          loading={playerValue === undefined}
                          disabled={!withWritePermission}
                          variant="ghost"
                        >
                          Override Default
                        </Button>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <Switch
                            checked={playerValue}
                            disabled={!withWritePermission}
                            onCheckedChange={checked => setPermissions({ ...permissions, [setting.id]: checked })}
                          />
                          <Tooltip>
                            <Tooltip.Trigger
                              render={
                                <Button
                                  onClick={() => setPermissions({ ...permissions, [setting.id]: null })}
                                  disabled={!withWritePermission}
                                  size="icon"
                                  className="rounded-full"
                                  variant="outline"
                                >
                                  <X />
                                </Button>
                              }
                            />
                            <Tooltip.Content>Remove Override</Tooltip.Content>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </FormCard.Content>
      <FormCard.Actions>
        <Button
          loading={saving}
          disabled={!withWritePermission || loading || permissionUpdates.length === 0}
          onClick={saveSettings}
        >
          Save
        </Button>
      </FormCard.Actions>
    </FormCard>
  );
}
